import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { Stack, Button, IconButton, Tooltip, Typography, Menu, MenuItem, Box } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import FavoriteIcon from '@mui/icons-material/Favorite';
import SearchIcon from '@mui/icons-material/Search';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SpeedIcon from '@mui/icons-material/Speed';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import OptimizedTooltip from './OptimizedTooltip';
import VersionManagementDialog from './VersionManagementDialog';
import { NumberKeyState, MButtonState, ABPoints } from '../types';
import { importVBIDsFromFile, exportVBIDsToFile, getCurrentVersionInfo, createNewVersionReset, createNewVersionKeep, enableCollaborativeMode } from '../utils/vbidIO';

// 事件處理器類型定義
interface NumberKeyHandlers {
  onNumberKeyClick: (keyNo: number) => void;
  onNoteDisplayChange?: (keyNo: number) => void; // 🔧 新增：只切換 Note 顯示，不觸發跳轉
  onDeleteModeToggle: () => void;
  onSettings: () => void;
  onMButtonClick: () => void;
  onModeChange: (newMode: MButtonState) => void;
  onNoteToggle: () => void;
  onCheckClick: () => void;
  onDeleteButtonClick: () => void;
  onDeleteButtonPress: (e: React.MouseEvent) => void;
  onDeleteButtonRelease: (e: React.MouseEvent) => void;
  onBookmarkClick: () => void;
  onSwitchClick: () => void;
  onBButtonClick: () => void;
  onSpeedChange: (speed: number) => void;
  onThemeDialogOpen?: (key: number) => void;
  onOpenNoteRecords?: () => void;
  onRequestLogin?: () => void;
  onVersionChange?: (newDocId: string) => void;
  videoUrl?: string;
  user?: any;
}

// 配置選項類型定義
interface NumberKeyConfig {
  showTimecode: boolean;
  isUserLoggedIn: boolean;
  maxNumberKeys: number;
  keySetIndex: number;
  ppPlayDuration: number;
  playbackRate: number;
  multiTimestampCounts?: Record<string, number>;
  hasSRTCache?: boolean;
  cachedSRTLanguage?: string;
  isAutoFillMode?: boolean;
  showAITooltip?: boolean;
}

// 簡化後的 Props 接口
interface NumberKeyPanelProps {
  // 核心數據
  numberKeys: Record<string, NumberKeyState>;
  isDeleteMode: boolean;
  mButtonState: MButtonState;
  vbids: ABPoints[];
  
  // 事件處理器（統一對象）
  handlers: NumberKeyHandlers;
  
  // 配置選項（統一對象）
  config: NumberKeyConfig;
  
  // 工具函數
  getSButtonStyle: () => any;
  setVbids: (vbids: ABPoints[]) => void;
  exportVBIDsToFile?: (vbids: ABPoints[]) => void;
  exportFirestoreData?: () => void;
  
  // 版本管理相關
  videoUrl?: string;
  user?: any;
  
  // 🔧 新增：當前播放時間（用於經過時間點放大動畫）
  currentTime?: number;
  
  // 🔧 新增：是否正在播放（用於判斷是否自動觸發）
  isPlaying?: boolean;
  
  // 🔧 新增：是否自動顯示 Note 內容（當播放經過重點時）
  autoShowNoteOnPass?: boolean;
}

const NumberKeyPanel: React.FC<NumberKeyPanelProps> = ({
  numberKeys,
  isDeleteMode,
  mButtonState,
  vbids,
  handlers,
  config,
  getSButtonStyle,
  setVbids,
  exportVBIDsToFile,
  exportFirestoreData,
  videoUrl,
  user,
  currentTime = 0,  // 🔧 新增：接收當前播放時間
  isPlaying = false,  // 🔧 新增：接收播放狀態
  autoShowNoteOnPass = false  // 🔧 修改：預設關閉自動顯示 Note，避免干擾手動操作
}) => {
  // 從 handlers 中解構事件處理函數
  const {
    onNumberKeyClick,
    onNoteDisplayChange, // 🔧 新增：只切換 Note 顯示的回調
    onDeleteModeToggle,
    onSettings,
    onMButtonClick,
    onModeChange,
    onNoteToggle,
    onCheckClick,
    onDeleteButtonClick,
    onDeleteButtonPress,
    onDeleteButtonRelease,
    onBookmarkClick,
    onSwitchClick,
    onBButtonClick,
    onSpeedChange,
    onThemeDialogOpen,
    onOpenNoteRecords,
    onRequestLogin,
    onVersionChange
  } = handlers;

  // 從 config 中解構配置選項
  const {
    showTimecode,
    isUserLoggedIn,
    maxNumberKeys,
    keySetIndex,
    ppPlayDuration,
    playbackRate,
    multiTimestampCounts
  } = config;
  // Speed選單狀態
  const [speedAnchorEl, setSpeedAnchorEl] = useState<null | HTMLElement>(null);
  const isSpeedOpen = Boolean(speedAnchorEl);
  // 記錄上次非1x速度
  const lastNonOneSpeed = useRef<number>(1);
  const clickCountRef = useRef(0);
  const lastTargetRef = useRef<EventTarget | null>(null);
  
  // 新增 Speed 按鈕狀態管理
  const [speedButtonState, setSpeedButtonState] = useState<'normal' | 'active' | 'turbo'>('normal');
  const [savedSpeed, setSavedSpeed] = useState<number>(1);
  const speedLongPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const speedClickTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  const [deleteActive, setDeleteActive] = useState(false);
  const [deleteMode, setDeleteMode] = useState<'none' | 'single' | 'multiple' | 'longPress'>('none');
  const deleteTimerRef = useRef<NodeJS.Timeout | null>(null);
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [mMenuAnchorEl, setMMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMMenuOpen = Boolean(mMenuAnchorEl);
  
  // 🔧 新增：追蹤當前放大的按鈕（經過時間點放大動畫）
  const [enlargedKey, setEnlargedKey] = useState<number | null>(null);
  const enlargeTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastPassedKeysRef = useRef<Set<number>>(new Set());
  const lastIsPlayingRef = useRef<boolean>(false);
  
  // 版本管理相關狀態
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [currentVersion, setCurrentVersion] = useState('v1');
  const [isLoadingVersion, setIsLoadingVersion] = useState(false);
  
  // 🔧 新增：監聽播放狀態變化，暫停時立即恢復按鈕大小
  useEffect(() => {
    // 記錄播放狀態
    const wasPlaying = lastIsPlayingRef.current;
    lastIsPlayingRef.current = isPlaying;
    
    // 🔧 關鍵：當從播放變為暫停時，立即恢復按鈕大小
    if (wasPlaying && !isPlaying && enlargedKey !== null) {
      // 調試日誌已移除
      setEnlargedKey(null);
      
      // 清除計時器
      if (enlargeTimerRef.current) {
        clearTimeout(enlargeTimerRef.current);
        enlargeTimerRef.current = null;
      }
    }
  }, [isPlaying, enlargedKey]);
  
  // 🔧 新增：監聽當前播放時間，實現「經過時間點放大」動畫
  useEffect(() => {
    // 🔧 優化：簡化條件檢查，減少不必要的日誌
    if (!currentTime || currentTime === 0 || !numberKeys || Object.keys(numberKeys).length === 0) {
      return; // 靜默返回，不輸出日誌
    }
    
    // 🔧 優化：減少調試日誌頻率（每30秒一次，而非每10秒）
    // 只在播放時輸出，暫停時不輸出
    if (isPlaying && Math.floor(currentTime) % 30 === 0 && Math.abs(currentTime - Math.floor(currentTime)) < 0.5) {
      // 調試日誌已移除
    }
    
    // 🔧 優化：擴大時間窗口，提高觸發穩定性
    // 觸發窗口：重點前 0.1 秒到重點後 0.5 秒（總共 0.6 秒窗口）
    const TRIGGER_WINDOW_BEFORE = 0.1;  // 重點前 0.1 秒開始觸發
    const TRIGGER_WINDOW_AFTER = 0.5;   // 重點後 0.5 秒內可觸發
    const RESET_DISTANCE = 2.0;          // 距離重點超過 2 秒時重置，允許下次觸發
    
    // 檢查是否有任何按鈕的時間點正在經過
    Object.entries(numberKeys).forEach(([keyStr, keyState]) => {
      if (!keyState.isActive || !keyState.timestamp) return;
      
      const keyNo = parseInt(keyStr);
      const timestamp = keyState.timestamp;
      const timeDiff = currentTime - timestamp;
      
      // 🔧 優化：在重點前後都設置觸發窗口
      const isInTriggerWindow = timeDiff >= -TRIGGER_WINDOW_BEFORE && timeDiff <= TRIGGER_WINDOW_AFTER;
      const isFarAway = Math.abs(timeDiff) > RESET_DISTANCE;
      
      // 當播放時間進入觸發窗口
      if (isInTriggerWindow) {
        // 🔧 簡化邏輯：如果按鈕還沒有放大，且還沒有處理過，則觸發放大
        const alreadyEnlarged = enlargedKey === keyNo;
        const alreadyProcessed = lastPassedKeysRef.current.has(keyNo);
        
        // 🔧 關鍵修復：只在播放狀態下才觸發放大動畫
        if (!alreadyEnlarged && !alreadyProcessed && isPlaying) {
          // 調試日誌已移除
          
          // 標記為已處理
          lastPassedKeysRef.current.add(keyNo);
          
          // 清除之前的計時器（如果有的話）
          if (enlargeTimerRef.current) {
            clearTimeout(enlargeTimerRef.current);
          }
          
          // 設置放大狀態
          setEnlargedKey(keyNo);
          
          // 🔧 新增：自動滾動放大的按鈕到中央位置（與 Pickup 效果相同）
          setTimeout(() => {
            // 調試日誌已移除
            scrollToNumberKey(keyNo);
          }, 150); // 延遲 150ms，與點擊數字鍵的滾動邏輯一致
          
          // 🔧 關鍵修改：使用新的 onNoteDisplayChange 回調，只切換 Note 顯示，不觸發跳轉
          if (mButtonState === 'M') {
            if (onNoteDisplayChange) {
              // 調試日誌已移除
              setTimeout(() => {
                onNoteDisplayChange(keyNo);
              }, 100);
            } else {
              console.log(`⚠️ [放大動畫] M 模式但缺少 onNoteDisplayChange 回調，無法切換 Note`);
            }
          } else {
            // 調試日誌已移除
          }
          
          // 🔧 修改：2.5 秒後恢復，但如果播放已中止則立即恢復（由上面的 useEffect 處理）
          enlargeTimerRef.current = setTimeout(() => {
            setEnlargedKey(null);
          }, 2500);
        } else if (!isPlaying && !alreadyEnlarged && !alreadyProcessed) {
          // 調試日誌已移除
        }
      }
      
      // 🔧 優化：當播放時間遠離重點足夠遠時，重置狀態，允許下次再次觸發
      if (isFarAway) {
        if (lastPassedKeysRef.current.has(keyNo)) {
          // 調試日誌已移除
          lastPassedKeysRef.current.delete(keyNo);
        }
      }
    });
    
    // 清理函數
    return () => {
      if (enlargeTimerRef.current) {
        clearTimeout(enlargeTimerRef.current);
      }
    };
  }, [currentTime, numberKeys, enlargedKey, isPlaying, autoShowNoteOnPass, mButtonState, onNumberKeyClick]);
  
  // 滾動容器引用
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // 觸控滾動狀態
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [isScrolling, setIsScrolling] = useState(false);

  // 自動滾動到指定數字鍵的中間位置
  const scrollToNumberKey = useCallback((targetKeyNo: number) => {
    // 調試日誌已移除
    
    if (!scrollContainerRef.current) {
      // 滾動計算日誌已移除
      return;
    }
    
    const container = scrollContainerRef.current;
    const containerWidth = container.clientWidth;
    const buttonWidth = 40; // 按鈕寬度
    const buttonGap = 8; // 按鈕間距
    const buttonTotalWidth = buttonWidth + buttonGap;
    
    // 滾動計算日誌已移除
    
    // 計算目標按鈕的位置
    const targetIndex = targetKeyNo - 1;
    const targetPosition = targetIndex * buttonTotalWidth;
    
    // 計算滾動位置，讓目標按鈕居中
    // 目標：讓目標數字鍵在容器中間
    const centerOffset = (containerWidth - buttonWidth) / 2;
    const scrollPosition = targetPosition - centerOffset;
    
    // 確保滾動位置在有效範圍內
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    const finalScrollPosition = Math.max(0, Math.min(scrollPosition, maxScrollLeft));
    
    // 滾動計算日誌已移除
    
    // 平滑滾動到目標位置
    container.scrollTo({
      left: finalScrollPosition,
      behavior: 'smooth'
    });
  }, []);

  // 🔧 優化：監聽數字鍵狀態變化，減少不必要的滾動
  const lastScrollTargetRef = useRef<number | null>(null);
  
  useEffect(() => {
    // 調試日誌已移除
    
    if (!numberKeys || Object.keys(numberKeys).length === 0) {
      console.log('❌ numberKeys 為空');
      return;
    }
    
    // 優先找到當前被 pickup 的數字鍵（isPickUpState = true）
    const pickupKeys = Object.entries(numberKeys)
      .filter(([keyStr, keyState]) => keyState && keyState.isPickUpState)
      .map(([keyStr]) => parseInt(keyStr))
      .sort((a, b) => a - b); // 升序排列，最小的在前
    
    if (pickupKeys.length > 0) {
      // 如果有被 pickup 的鍵，優先滾動到它
      const pickupKey = pickupKeys[0];
      
      // 🔧 優化：避免重複滾動到同一個目標
      if (lastScrollTargetRef.current !== pickupKey) {
        // 調試日誌已移除
        lastScrollTargetRef.current = pickupKey;
        
        // 延遲滾動，確保 DOM 已更新
        setTimeout(() => scrollToNumberKey(pickupKey), 150);
      }
    } else {
      // 如果沒有被 pickup 的鍵，找最新標記的數字鍵（數字最大的活躍鍵）
      const activeKeys = Object.entries(numberKeys)
        .filter(([keyStr, keyState]) => keyState && keyState.isActive)
        .map(([keyStr]) => parseInt(keyStr))
        .sort((a, b) => b - a); // 降序排列，最大的在前
      
      // 🔧 優化：減少日誌輸出頻率
      // 調試日誌已移除
      
      if (activeKeys.length > 0) {
        const latestActiveKey = activeKeys[0]; // 取最大的數字鍵
        
        // 🔧 優化：避免重複滾動到同一個目標
        if (lastScrollTargetRef.current !== latestActiveKey) {
          // 🔧 優化：減少日誌輸出頻率
          // 調試日誌已移除
          lastScrollTargetRef.current = latestActiveKey;
          
          // 延遲滾動，確保 DOM 已更新
          setTimeout(() => scrollToNumberKey(latestActiveKey), 150);
        }
      } else {
        // 🔧 優化：減少日誌輸出頻率
        // console.log('❌ 沒有找到活躍的數字鍵');
        lastScrollTargetRef.current = null;
      }
    }
  }, [numberKeys, scrollToNumberKey]);

  // 重置刪除模式
  const resetDeleteMode = () => {
    setDeleteActive(false);
    setDeleteMode('none');
    onDeleteModeToggle();
    if (deleteTimerRef.current) {
      clearTimeout(deleteTimerRef.current);
      deleteTimerRef.current = null;
    }
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  // 觸控事件處理 - 修復版本
  const handleTouchStart = (e: React.TouchEvent) => {
    // 檢查是否在輸入框區域，如果是則不阻止默認行為
    const target = e.target as HTMLElement;
    const isInputArea = target.closest('input') || target.closest('textarea') || target.closest('[contenteditable="true"]');
    
    if (!isInputArea) {
      e.preventDefault(); // 只在非輸入框區域防止默認行為
    }
    // 調試日誌已移除
    setTouchStartX(e.touches[0].clientX);
    setTouchStartY(e.touches[0].clientY);
    setIsScrolling(false);
  };
  
  const handleTouchMove = (e: React.TouchEvent) => {
    // 檢查是否在輸入框區域，如果是則不阻止默認行為
    const target = e.target as HTMLElement;
    const isInputArea = target.closest('input') || target.closest('textarea') || target.closest('[contenteditable="true"]');
    
    if (!touchStartX || !touchStartY) return;
    
    const touchCurrentX = e.touches[0].clientX;
    const touchCurrentY = e.touches[0].clientY;
    
    const deltaX = touchCurrentX - touchStartX;
    const deltaY = touchCurrentY - touchStartY;
    
    // 調試日誌已移除
    
    // 檢測水平滾動
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 5) {
      // 只有在確認是水平滾動且不在輸入框區域時才阻止默認行為
      if (!isInputArea) {
        e.preventDefault();
      }
      setIsScrolling(true);
      
      const container = scrollContainerRef.current;
      if (container) {
        // 計算滾動距離
        const scrollAmount = -deltaX * 1.5;
        const newScrollLeft = Math.max(0, Math.min(
          container.scrollLeft + scrollAmount,
          container.scrollWidth - container.clientWidth
        ));
        
        console.log('📜 滾動:', {
          current: container.scrollLeft,
          new: newScrollLeft,
          delta: scrollAmount,
          max: container.scrollWidth - container.clientWidth,
          containerWidth: container.clientWidth,
          scrollWidth: container.scrollWidth
        });
        
        // 使用 requestAnimationFrame 確保滾動流暢
        requestAnimationFrame(() => {
          container.scrollLeft = newScrollLeft;
        });
        
        // 更新觸控起始位置，實現連續滾動
        setTouchStartX(touchCurrentX);
        setTouchStartY(touchCurrentY);
      }
    }
  };
  
  const handleTouchEnd = () => {
    // 調試日誌已移除
    setTouchStartX(null);
    setTouchStartY(null);
    setIsScrolling(false);
  };

  // 開始刪除計時器
  const startDeleteTimer = () => {
    if (deleteTimerRef.current) {
      clearTimeout(deleteTimerRef.current);
    }
    deleteTimerRef.current = setTimeout(() => {
      resetDeleteMode();
    }, 3000);
  };

  // 處理長按刪除所有
  const handleLongPressDelete = () => {
    setDeleteMode('longPress');
    // 調用父組件的長按清除所有功能
    onDeleteButtonPress({} as React.MouseEvent);
    resetDeleteMode();
  };

  // 處理 D 按鈕按下
  const handleDeleteButtonMouseDown = () => {
    if (deleteMode === 'none') {
      // 開始長按計時器
      longPressTimerRef.current = setTimeout(() => {
        handleLongPressDelete();
      }, 2000);
    }
  };

  // 處理 D 按鈕釋放
  const handleDeleteButtonMouseUp = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  // 處理 D 按鈕離開
  const handleDeleteButtonMouseLeave = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  // 清理計時器
  React.useEffect(() => {
    return () => {
      if (deleteTimerRef.current) {
        clearTimeout(deleteTimerRef.current);
      }
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
      if (speedLongPressTimerRef.current) {
        clearTimeout(speedLongPressTimerRef.current);
      }
      if (speedClickTimerRef.current) {
        clearTimeout(speedClickTimerRef.current);
      }
    };
  }, []);

  // 播放速度切換時，記錄最近一次非1x速度
  const wrappedHandleSpeedChange = (speed: number) => {
    if (speed !== 1) lastNonOneSpeed.current = speed;
    onSpeedChange(speed);
  };

  // 處理 Speed 按鈕長按
  const handleSpeedLongPress = () => {
    setSpeedAnchorEl(document.querySelector('[data-speed-button]') as HTMLElement);
  };

  // 處理 Speed 按鈕按下
  const handleSpeedMouseDown = () => {
    speedLongPressTimerRef.current = setTimeout(() => {
      handleSpeedLongPress();
    }, 500); // 500ms 長按觸發
  };

  // 處理 Speed 按鈕釋放
  const handleSpeedMouseUp = () => {
    if (speedLongPressTimerRef.current) {
      clearTimeout(speedLongPressTimerRef.current);
      speedLongPressTimerRef.current = null;
    }
  };

  // 處理 Speed 按鈕離開
  const handleSpeedMouseLeave = () => {
    if (speedLongPressTimerRef.current) {
      clearTimeout(speedLongPressTimerRef.current);
      speedLongPressTimerRef.current = null;
    }
  };

  // 處理 Speed 按鈕點擊/雙擊
  const handleSpeedButton = (event: React.MouseEvent<HTMLElement>) => {
    lastTargetRef.current = event.currentTarget;
    clickCountRef.current++;
    
    if (speedClickTimerRef.current) {
      clearTimeout(speedClickTimerRef.current);
    }
    
    speedClickTimerRef.current = setTimeout(() => {
      if (clickCountRef.current === 1) {
        // 單擊：變速開關
        if (speedButtonState === 'normal') {
          // 開啟變速模式，使用上次保存的速度或預設 1.5x
          const targetSpeed = savedSpeed !== 1 ? savedSpeed : 1.5;
          setSpeedButtonState('active');
          setSavedSpeed(playbackRate);
          wrappedHandleSpeedChange(targetSpeed);
        } else if (speedButtonState === 'active') {
          // 關閉變速模式，回到 1x
          setSpeedButtonState('normal');
          setSavedSpeed(playbackRate);
          wrappedHandleSpeedChange(1);
        } else if (speedButtonState === 'turbo') {
          // 從 turbo 模式回到 active 模式
          setSpeedButtonState('active');
          const targetSpeed = savedSpeed !== 1 ? savedSpeed : 1.5;
          wrappedHandleSpeedChange(targetSpeed);
        }
      } else if (clickCountRef.current === 2) {
        // 雙擊：快速切換到 2x
        if (speedButtonState === 'turbo') {
          // 從 turbo 模式回到 active 模式
          setSpeedButtonState('active');
          const targetSpeed = savedSpeed !== 1 ? savedSpeed : 1.5;
          wrappedHandleSpeedChange(targetSpeed);
        } else {
          // 進入 turbo 模式
          setSpeedButtonState('turbo');
          setSavedSpeed(playbackRate);
          wrappedHandleSpeedChange(2);
        }
      }
      clickCountRef.current = 0;
      lastTargetRef.current = null;
    }, 200);
  };

  // 處理 Speed 選單關閉
  const handleSpeedClose = () => {
    setSpeedAnchorEl(null);
  };

  // 處理速度選擇
  const handleSpeedSelect = (speed: number) => {
    wrappedHandleSpeedChange(speed);
    // 如果當前是 active 模式，更新保存的速度
    if (speedButtonState === 'active') {
      setSavedSpeed(speed);
    }
    handleSpeedClose();
  };

  // 速度選項（由大到小排列）
  const speedOptions = [2, 1.75, 1.5, 1.25, 1, 0.75, 0.5];

  // 計算實際需要的按鈕數量（基於活躍的檢查點）
  const actualMaxKeys = useMemo(() => {
    const safeMaxNumberKeys = maxNumberKeys || 18;
    
    // 🔧 修復：正確計算最大鍵號，包括所有有狀態的鍵
    const allKeys = Object.entries(numberKeys || {})
      .map(([key]) => parseInt(key))
      .filter(key => !isNaN(key))
      .sort((a, b) => a - b);
    
    const maxActiveKey = allKeys.length > 0 ? Math.max(...allKeys) : 0;
    
    // 🔧 修改：顯示所有實際需要的數字鍵，不強制限制在18個
    const actualMaxKeys = Math.max(safeMaxNumberKeys, maxActiveKey);
    
    // 🔧 調試：顯示數字鍵計算過程
    // 調試日誌已移除
    
    return actualMaxKeys;
  }, [maxNumberKeys, numberKeys]);
  
  // 監聽數字鍵狀態變化，自動滾動到藍色鍵
  // 移除重複的滾動邏輯，避免與主要的滾動邏輯衝突
  // React.useEffect(() => {
  //   // 當有新的藍色鍵激活時，自動滾動到該鍵
  //   const activeKeys = Object.entries(numberKeys || {})
  //     .filter(([key, state]) => state.isActive)
  //     .map(([key]) => parseInt(key))
  //     .sort((a, b) => a - b);
    
  //   if (activeKeys.length > 0) {
  //     // 延遲滾動，確保 DOM 已更新
  //     setTimeout(() => {
  //       scrollToNumberKey(activeKeys[0]);
  //     }, 100);
  //   }
  // }, [numberKeys]); // 依賴 numberKeys 變化



  // 處理 M 按鈕點擊（打開選單）
  const handleMButtonMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setMMenuAnchorEl(event.currentTarget);
  };

  // 處理選單關閉
  const handleMMenuClose = () => {
    setMMenuAnchorEl(null);
  };

  // 處理模式選擇
  const handleModeSelect = (selectedMode: MButtonState) => {
    // 如果選擇的模式與當前模式相同，不做任何操作
    if (selectedMode === mButtonState) {
      setMMenuAnchorEl(null);
      return;
    }

    // 調用父組件的模式切換邏輯
    onModeChange(selectedMode);
    setMMenuAnchorEl(null);
  };

  // 調試日誌已移除
  const safeOnOpenNoteRecords = useCallback(() => {
    // 調試日誌已移除
  }, []);

  // 版本管理相關函數
  const handleVersionManagement = useCallback(async () => {
    if (!videoUrl || !user?.email) {
      console.log('❌ 版本管理需要登入用戶和影片URL');
      return;
    }

    setIsLoadingVersion(true);
    try {
      // 提取影片ID
      let videoId = videoUrl;
      if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
        const extractYouTubeId = (url: string): string | null => {
          const match = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/|live\/)([a-zA-Z0-9_-]{11})/);
          return match ? match[1] : null;
        };
        const extractedId = extractYouTubeId(videoUrl);
        if (extractedId) {
          videoId = extractedId;
        }
      }

      // 獲取當前版本信息
      const versionInfo = await getCurrentVersionInfo(videoId, user.email);
      if (versionInfo) {
        setCurrentVersion(versionInfo.currentVersion);
        setVersionDialogOpen(true);
      } else {
        setCurrentVersion('v1');
        setVersionDialogOpen(true);
      }
    } catch (error) {
      console.error('❌ 獲取版本信息失敗:', error);
      setCurrentVersion('v1');
      setVersionDialogOpen(true);
    } finally {
      setIsLoadingVersion(false);
    }
  }, [videoUrl, user]);

  const handleVersionAction = useCallback(async (
    action: 'create_new_reset' | 'create_new_keep' | 'collaborative',
    version: string
  ) => {
    if (!videoUrl || !user?.email) {
      console.log('❌ 版本操作需要登入用戶和影片URL');
      return;
    }

    try {
      // 提取影片ID
      let videoId = videoUrl;
      if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
        const extractYouTubeId = (url: string): string | null => {
          const match = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/|live\/)([a-zA-Z0-9_-]{11})/);
          return match ? match[1] : null;
        };
        const extractedId = extractYouTubeId(videoUrl);
        if (extractedId) {
          videoId = extractedId;
        }
      }

      let newDocId: string | null = null;

      switch (action) {
        case 'create_new_reset':
          newDocId = await createNewVersionReset(videoId, user.email);
          // 調試日誌已移除
          break;
        case 'create_new_keep':
          newDocId = await createNewVersionKeep(videoId, user.email);
          // 調試日誌已移除
          break;
        case 'collaborative':
          await enableCollaborativeMode(videoId, user.email, currentVersion);
          // 調試日誌已移除
          break;
      }

      // 通知父組件版本已變更
      if (newDocId && onVersionChange) {
        onVersionChange(newDocId);
      }

      // 重新載入頁面以更新數據
      if (action !== 'collaborative') {
        window.location.reload();
      }
    } catch (error) {
      console.error('❌ 版本操作失敗:', error);
    }
  }, [videoUrl, user, currentVersion, onVersionChange]);

  return (
    <Stack spacing={0.5} sx={{ 
      mt: 0.5, // 修改：從1改為0.5，進一步縮小上方間距
      mb: 0.5, // 修改：從1改為0.5，進一步縮小下方間距
      // 添加滾動動畫
      '@keyframes pulse': {
        '0%': { opacity: 1 },
        '50%': { opacity: 0.5 },
        '100%': { opacity: 1 }
      }
    }}>
      {/* Control buttons row */}
      <Stack direction="row" spacing={1} sx={{ 
        justifyContent: 'center',
        mb: 0.25 // 修改：從0.5改為0.25，進一步縮小與下方淺紅色區的間距
      }}>
        {/* FA Button */}
        <Button
          variant="outlined"
          size="small"
          sx={{ 
            minWidth: '40px', 
            height: '36px', 
            borderRadius: '8px', 
            fontWeight: 500, 
            fontSize: '1rem', 
            mr: 1,
            backgroundColor: 'white', // 預設背景維持白色
            '&:hover': {
              backgroundColor: '#f5f5f5',
              borderColor: '#1976d2',
            }
          }}
          onClick={() => {
            // 根據不同模式執行不同功能
            switch (mButtonState) {
              case 'M':
                // M 模式下，N 鍵開啟版本管理或 Note 記錄視窗
                // 檢查是否在直式佈局
                const isPortrait = window.innerWidth <= 800 && window.innerHeight >= 700;
                
                if (isPortrait) {
                  // 直式佈局：直接開啟 Note 記錄管理視窗
                  // 調試日誌已移除
                  if (onOpenNoteRecords) {
                    onOpenNoteRecords();
                  } else {
                    safeOnOpenNoteRecords();
                  }
                } else {
                  // 🔧 修復：橫式佈局統一行為 - 所有情況都開啟 Note 記錄視窗
                  // 不再使用舊的版本管理功能
                  // 調試日誌已移除
                  if (onOpenNoteRecords) {
                    onOpenNoteRecords();
                  } else {
                    safeOnOpenNoteRecords();
                  }
                }
                break;
              case 'AB':
                // AB重複播放 5次
                console.log('AB重複播放 5次');
                break;
              case 'MS':
                // 配合選取數字鍵(起點)所設的結束點的時間戳記
                // 調試日誌已移除
                break;
              case 'PB':
                // 重點播3秒
                console.log('重點播3秒');
                break;
              case 'MV':
                // MV模式的主題功能
                console.log('MV模式 - 主題功能');
                console.log('numberKeys:', numberKeys);
                console.log('handleThemeDialogOpen:', onThemeDialogOpen);
                
                // 找到當前有URL的數字鍵，如果沒有則使用第一個數字鍵
                const activeKeys = Object.entries(numberKeys)
                  .filter(([key, state]) => state.isActive && state.mvUrl)
                  .map(([key]) => parseInt(key))
                  .sort((a, b) => a - b);
                console.log('activeKeys:', activeKeys);
                
                // 如果有記錄的URL，使用第一個；否則使用數字鍵1
                const keyToSet = activeKeys.length > 0 ? activeKeys[0] : 1;
                console.log('keyToSet:', keyToSet);
                
                if (onThemeDialogOpen) {
                  console.log('調用 onThemeDialogOpen');
                  onThemeDialogOpen(keyToSet);
                } else {
                  console.log('onThemeDialogOpen 是 undefined');
                }
                break;
            }
          }}
        >
          {mButtonState === 'M' ? 'N' :
           mButtonState === 'AB' ? '5' :
           mButtonState === 'MS' ? 'Out' :
           mButtonState === 'PB' ? '3' :
           mButtonState === 'MV' ? '主題' : 'FA'}
        </Button>
        {/* FB Button / AI Button */}
        <Tooltip
          title="啟動字幕，雙擊紅色可自動填充"
          {...(config.showAITooltip === true ? { open: true } : {})}
          arrow
          placement="top"
        >
          <Button
            variant="outlined"
            size="small"
            sx={{ 
              minWidth: '40px', 
              height: '36px', 
              borderRadius: '8px', 
              fontWeight: 500, 
              fontSize: '1rem', 
              ml: 1,
              // 🔧 新增：智能 AI 按鈕顏色邏輯
              backgroundColor: (mButtonState === 'M' && config.hasSRTCache) ? 
                (config.isAutoFillMode ? '#f44336' : '#1976d2') : 'white',
              color: (mButtonState === 'M' && config.hasSRTCache) ? 'white' : 'inherit',
              borderColor: (mButtonState === 'M' && config.hasSRTCache) ? 
                (config.isAutoFillMode ? '#f44336' : '#1976d2') : 'rgba(0, 0, 0, 0.23)',
              '&:hover': {
                backgroundColor: (mButtonState === 'M' && config.hasSRTCache) ? 
                  (config.isAutoFillMode ? '#d32f2f' : '#1565c0') : 'rgba(0, 0, 0, 0.04)',
                borderColor: (mButtonState === 'M' && config.hasSRTCache) ? 
                  (config.isAutoFillMode ? '#d32f2f' : '#1565c0') : 'rgba(0, 0, 0, 0.23)',
              }
            }}
            onClick={() => {
              // 根據不同模式執行不同功能
              switch (mButtonState) {
                case 'M':
                  // 🔧 修改：顯示 SRT 字幕內容
                  console.log('🤖 AI 按鈕被點擊，顯示 SRT 字幕內容');
                  if (onMButtonClick) {
                    onMButtonClick();
                  }
                  break;
              case 'AB':
                // 無限循環
                console.log('無限循環');
                break;
              case 'MS':
                // SET同設置完成確定的意思
                // 調試日誌已移除
                break;
              case 'PB':
                // 重點播8秒
                console.log('重點播8秒');
                break;
              case 'MV':
                // MV模式的主題功能
                console.log('MV模式 - 主題功能');
                // 找到當前有URL的數字鍵，如果沒有則使用第一個數字鍵
                const activeKeys = Object.entries(numberKeys)
                  .filter(([key, state]) => state.isActive && state.mvUrl)
                  .map(([key]) => parseInt(key))
                  .sort((a, b) => a - b);
                
                // 如果有記錄的URL，使用第一個；否則使用數字鍵1
                const keyToSet = activeKeys.length > 0 ? activeKeys[0] : 1;
                if (onThemeDialogOpen) {
                  onThemeDialogOpen(keyToSet);
                }
                break;
            }
          }}
        >
          {mButtonState === 'M' ? 'AI' :
           mButtonState === 'AB' ? '∞' :
           mButtonState === 'MS' ? 'SET' :
           mButtonState === 'PB' ? '8' :
           mButtonState === 'MV' ? '上傳' : 'FB'}
        </Button>
        </Tooltip>
        {/* M/AB/PB button */}
        <IconButton
          onClick={handleMButtonMenuClick}
          sx={{
            minWidth: '40px',
            height: '36px',
            backgroundColor: isDeleteMode ? 'transparent' : 
              (mButtonState === 'M' ? '#ffffff' : 
                mButtonState === 'AB' ? '#ffc107' : 
                mButtonState === 'MS' ? '#008888' : 
                '#CC0000'),
            border: '1px solid',
            borderColor: isDeleteMode ? '#e0e0e0' : 
              (mButtonState === 'M' ? '#1976d2' : 
                mButtonState === 'AB' ? '#ffc107' : 
                mButtonState === 'MS' ? '#008888' : 
                '#880000'),
            borderRadius: '8px',
            color: isDeleteMode ? 'rgba(0, 0, 0, 0.54)' : 
              (mButtonState === 'M' ? '#1976d2' : 'white'),
            '&:hover': {
              backgroundColor: isDeleteMode ? '#f5f5f5' : 
                (mButtonState === 'M' ? '#f5f5f5' : 
                  mButtonState === 'AB' ? '#ffa000' : 
                  mButtonState === 'MS' ? '#006666' : 
                  mButtonState === 'MV' ? '#7b1fa2' :
                  '#CC0000')
            }
          }}
        >
          <Typography 
            variant="body2" 
            sx={{ fontWeight: 500, fontSize: '1rem' }}
          >
            {mButtonState === 'M' ? '模式' : 
              mButtonState === 'AB' ? 'AB輪播' :
              mButtonState === 'MS' ? '短影音' :
              mButtonState === 'PB' ? `重點${ppPlayDuration}秒` :
              mButtonState === 'MV' ? 'MV' :
              mButtonState}
          </Typography>
        </IconButton>

        {/* M 按鈕選單 */}
        <Menu
          anchorEl={mMenuAnchorEl}
          open={isMMenuOpen}
          onClose={handleMMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <MenuItem 
            onClick={() => handleModeSelect('M')}
            sx={{
              backgroundColor: mButtonState === 'M' ? '#e3f2fd' : 'transparent',
              color: mButtonState === 'M' ? '#1976d2' : 'inherit',
              fontWeight: mButtonState === 'M' ? 600 : 400,
            }}
          >
            模式
          </MenuItem>
          <MenuItem 
            onClick={() => handleModeSelect('AB')}
            sx={{
              backgroundColor: mButtonState === 'AB' ? '#fff3e0' : 'transparent',
              color: mButtonState === 'AB' ? '#ff8f00' : 'inherit',
              fontWeight: mButtonState === 'AB' ? 600 : 400,
            }}
          >
            AB輪播
          </MenuItem>
          <MenuItem 
            onClick={() => handleModeSelect('PB')}
            sx={{
              backgroundColor: mButtonState === 'PB' ? '#ffebee' : 'transparent',
              color: mButtonState === 'PB' ? '#c62828' : 'inherit',
              fontWeight: mButtonState === 'PB' ? 600 : 400,
            }}
          >
            重點{ppPlayDuration}秒
          </MenuItem>
          <MenuItem 
            onClick={() => handleModeSelect('MS')}
            sx={{
              backgroundColor: mButtonState === 'MS' ? '#e0f2f1' : 'transparent',
              color: mButtonState === 'MS' ? '#00695c' : 'inherit',
              fontWeight: mButtonState === 'MS' ? 600 : 400,
            }}
          >
            短影音
          </MenuItem>
          <MenuItem 
            onClick={() => handleModeSelect('MV')}
            sx={{
              backgroundColor: mButtonState === 'MV' ? '#f3e5f5' : 'transparent',
              color: mButtonState === 'MV' ? '#7b1fa2' : 'inherit',
              fontWeight: mButtonState === 'MV' ? 600 : 400,
            }}
          >
            MV
          </MenuItem>
        </Menu>

        {/* Speed 選單 */}
        <Menu
          anchorEl={speedAnchorEl}
          open={isSpeedOpen}
          onClose={handleSpeedClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {speedOptions.map((speed) => (
            <MenuItem 
              key={speed}
              onClick={() => handleSpeedSelect(speed)}
              sx={{
                backgroundColor: playbackRate === speed ? '#e3f2fd' : 'transparent',
                color: playbackRate === speed ? '#1976d2' : 'inherit',
                fontWeight: playbackRate === speed ? 600 : 400,
              }}
            >
              {speed}x
            </MenuItem>
          ))}
        </Menu>

        {/* CHECK button */}
        <IconButton
          onClick={() => {
            // 🔧 修改：檢查用戶登入狀態，但按鈕保持正常外觀
            if (!isUserLoggedIn && onRequestLogin) {
              // 調試日誌已移除
              onRequestLogin();
              return;
            }
            onCheckClick();
          }}
          sx={{
            minWidth: '72px',
            width: '72px',
            height: '36px',
            backgroundColor: '#1976d2', // 保持正常藍色
            border: '1px solid',
            borderColor: '#1976d2',
            borderRadius: '8px',
            color: 'white',
            '&:hover': {
              backgroundColor: '#1565c0',
              borderColor: '#1565c0'
            }
          }}
        >
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 500, 
              fontSize: '1.2rem',
              lineHeight: 1.2,
              textAlign: 'center',
              whiteSpace: 'pre-line'
            }}
          >
            標記
          </Typography>
        </IconButton>

        {/* 我的重點紅球按鈕，移到 D 鍵左邊 */}
        <span>
          <IconButton 
            onClick={onBookmarkClick}
            sx={{ 
              minWidth: '46px', // 調整為更接近M鍵的實際寬度
              width: '46px', // 固定寬度
              height: '36px', // 與M鍵一致的高度
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#ffffff',
              border: '1px solid',
              borderColor: '#888888',
              borderRadius: '8px',
              p: 0
            }}
          >
            <FavoriteIcon sx={{ 
              color: '#d32f2f',
              fontSize: '1.3rem' 
            }} />
          </IconButton>
        </span>

        {/* D 鍵（刪除鍵） */}
        <IconButton
            onClick={() => {
              // 🔧 修改：檢查用戶登入狀態，但按鈕保持正常外觀
              if (!isUserLoggedIn && onRequestLogin) {
                console.log('未登入用戶嘗試使用刪除功能，顯示登入對話框');
                onRequestLogin();
                return;
              }
              if (isDeleteMode) {
                resetDeleteMode();
              } else {
                onDeleteButtonClick();
              }
            }}
            onMouseDown={handleDeleteButtonMouseDown}
            onMouseUp={handleDeleteButtonMouseUp}
            onMouseLeave={handleDeleteButtonMouseLeave}
            sx={{
              minWidth: '40px',
              height: '36px',
              backgroundColor: deleteMode !== 'none' ? '#ff0000' : '#ffffff', // 保持正常顏色
              border: '1px solid',
              borderColor: deleteMode !== 'none' ? '#ff0000' : '#888888',
              borderRadius: '8px',
              color: deleteMode !== 'none' ? '#ffffff' : '#ff1744',
              '&:hover': {
                backgroundColor: deleteMode !== 'none' ? '#cc0000' : '#f5f5f5',
                borderColor: deleteMode !== 'none' ? '#cc0000' : 'primary.main'
              }
            }}
          >
            <DeleteForeverIcon sx={{ 
              color: deleteMode === 'multiple' ? '#000000' : 
                     deleteMode === 'single' ? '#ffffff' : 
                     '#ff1744',
              fontSize: '1.6rem' 
            }} />
          </IconButton>

        {/* Speed button */}
        <IconButton
            data-speed-button
            onClick={handleSpeedButton}
            onMouseDown={handleSpeedMouseDown}
            onMouseUp={handleSpeedMouseUp}
            onMouseLeave={handleSpeedMouseLeave}
            sx={{ 
              minWidth: '40px',
              height: '36px',
              backgroundColor: speedButtonState === 'normal' ? '#ffffff' : 
                             speedButtonState === 'active' ? '#1976d2' : '#ff0000',
              border: '1.5px solid',
              borderColor: speedButtonState === 'normal' ? '#1976d2' : 
                          speedButtonState === 'active' ? '#1976d2' : '#ff0000',
              borderRadius: '8px',
              color: speedButtonState === 'normal' ? '#1976d2' : 'white',
              '&:hover': {
                backgroundColor: speedButtonState === 'normal' ? '#f5f5f5' : 
                              speedButtonState === 'active' ? '#1565c0' : '#cc0000',
                borderColor: speedButtonState === 'normal' ? '#1565c0' : 
                           speedButtonState === 'active' ? '#1565c0' : '#cc0000'
              },
              '& .MuiSvgIcon-root': { 
                color: speedButtonState === 'normal' ? '#1976d2' : 'white',
                fontSize: '1.2rem'
              }
            }}
          >
            <SpeedIcon />
          </IconButton>

        {/* Settings button - 暫時隱藏 */}
        {/* <Tooltip title="設定" enterDelay={200} leaveDelay={0}>
          <IconButton
            onClick={onSettings}
            sx={{ 
              minWidth: '40px',
              height: '36px',
              backgroundColor: '#ffffff',
              border: '1px solid',
              borderColor: '#888888',
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                borderColor: 'primary.main'
              },
              '& .MuiSvgIcon-root': { 
                color: '#666666',
                fontSize: '1.2rem'
              }
            }}
          >
            <SettingsIcon />
          </IconButton>
        </Tooltip> */}
      </Stack>

      {/* Number buttons row - 滾動式設計 */}
      <Box 
        sx={{ 
          position: 'relative', 
          // 使用絕對置中對齊，確保在畫面正中央
          left: '50%',
          transform: 'translateX(-50%)',
          // 強制設定數字容器寬度為380px（控制面板Group UI的95%）
          width: '380px',
          maxWidth: '380px',
          display: 'flex', // 使用 flexbox 確保子元素置中
          justifyContent: 'center', // 水平置中
          alignItems: 'center', // 垂直置中
          // 防止觸控事件衝突
          touchAction: 'pan-x',
          // 確保觸控滾動正常工作
          WebkitOverflowScrolling: 'touch',
          backgroundColor: '#ffebee', // 修改：淺藍色改成淺紅色
          borderRadius: '12px', // 新增：圓角設計
          border: '1px solid #ffcdd2', // 新增：淺紅色邊框
          minHeight: '50px', // 修改：最小高度設成50px

        }}
      >

        {/* 滾動容器 - 使用原生觸控滾動 */}
        <Box
          ref={scrollContainerRef}
          sx={{
            display: 'flex',
            overflowX: 'auto',
            scrollBehavior: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            gap: 1,
            padding: '8px 16px',
            // 充分利用外層容器寬度
            width: '100%', // 佔滿外層容器的100%寬度
            height: '100%', // 新增：高度響應外層容器
            // 手機直式布局時縮小容器最小高度
            minHeight: (() => {
              const isPortrait = window.innerWidth <= 800 && window.innerHeight >= 700;
              return isPortrait ? '34px' : '40px';
            })(), // 配合按鈕高度調整
            // 移除錯誤的 minWidth，讓容器自然限制寬度
            // 數字鍵會自動排列，超出容器寬度時自然產生滾動
            // 觸控優化 - 使用原生觸控滾動
            WebkitOverflowScrolling: 'touch',
            msOverflowStyle: 'none',
            touchAction: 'pan-x', // 允許水平觸控滾動
            userSelect: 'none',
            position: 'relative',
            overscrollBehavior: 'contain',
            // 置中對齊優化
            justifyContent: 'flex-start', // 數字鍵從左開始排列
            alignItems: 'center', // 垂直置中
            backgroundColor: '#fff5f5' // 修改：淺灰色改成更淺的紅色
          }}
        >
          {/* 所有數字鍵 - 不再分頁，全部顯示 */}
          {Array.from({ length: actualMaxKeys }, (_, index) => {
            const keyNo = index + 1;
            const keyStr = keyNo.toString();
            // 防護措施：確保 numberKeys 存在
            const safeNumberKeys = numberKeys || {};
            
            // 🔧 調試：檢查 numberKeys 狀態（已註釋以減少日誌輸出）
            // if (keyNo <= 8) {
            //   console.log(`🔍 NumberKeyPanel 按鈕 ${keyNo}:`, {
            //     keyStr,
            //     safeNumberKeys: safeNumberKeys[keyStr],
            //     hasKey: !!safeNumberKeys[keyStr],
            //     isActive: safeNumberKeys[keyStr]?.isActive
            //   });
            // }
            
            const keyState = safeNumberKeys[keyStr] || {
              keyNo: keyNo,
              isActive: false,
              isABPoint: false,
              isPickUpState: false,
              timestamp: null,
              timecode: '00:00:00 00',
              videoMode: null,
              frame: null,
              note: '',
              screenshot: '',
              flag: 'off'
            };
            const isABPoint = keyState.isABPoint;
            const buttonColor = isABPoint ? '#ffc107' :
                              isDeleteMode && keyState.isActive ? '#FF00FF' :
                              keyState.isPickUpState ? '#FF0000' :
                              keyState.isActive ? '#1976d2' :
                              '#e0e0e0';
            
            // 🔧 修復：移除過多的調試日誌，避免性能問題
            // if (keyNo === 5 || keyNo === 6 || keyNo === 7) {
            // 調試日誌已移除
            //     keyStr,
            //     keyState,
            //     isPickUpState: keyState.isPickUpState,
            //     buttonColor,
            //     hasKey: !!safeNumberKeys[keyStr]
            //   });
            // }
            const textColor = keyState.isActive ? 'white' : 'rgba(0, 0, 0, 0.54)';
            

            return (
              <span key={keyNo}>
                  <IconButton
                    data-key={keyNo} // 添加數據屬性用於滾動定位
                    onClick={() => {
                      // 🔧 調試日誌已移除，減少控制台輸出
                      // console.log(`🔍 數字鍵 ${keyNo} 點擊 - 調試信息:`);
                      // console.log(`  - isUserLoggedIn: ${isUserLoggedIn}`);
                      // console.log(`  - onRequestLogin 類型: ${typeof onRequestLogin}`);
                      // console.log(`  - onRequestLogin 值: ${onRequestLogin}`);
                      // console.log(`  - !!onRequestLogin: ${!!onRequestLogin}`);
                      // console.log(`  - keyState.isActive: ${keyState.isActive}`);
                      // console.log(`  - keyState.isPickUpState: ${keyState.isPickUpState}`);
                      // console.log(`  - isDeleteMode: ${isDeleteMode}`);
                      
                      // 🔧 檢查 onRequestLogin 函數是否被正確傳遞
                      // if (onRequestLogin) {
                      //   console.log('  - ✅ onRequestLogin 函數存在');
                      // } else {
                      //   console.log('  - ❌ onRequestLogin 函數不存在');
                      // }
                      
                      // 🔧 修正：未登入用戶只能點擊藍色 pickup 狀態的數字鍵，其他都需要登入
                      // 🔧 新增：直接檢查 Firebase 認證狀態，避免狀態不同步問題
                      const isActuallyLoggedIn = isUserLoggedIn;
                      if (!isActuallyLoggedIn && onRequestLogin) {
                        // 調試日誌已移除
                        
                        // 如果是刪除模式，需要登入
                        if (isDeleteMode) {
                          // 調試日誌已移除
                          console.log('未登入用戶嘗試使用刪除功能，顯示登入對話框');
                          console.log('  - 🔐 調用 onRequestLogin()');
                          onRequestLogin();
                          // 調試日誌已移除
                          return;
                        }
                        
                        // 如果是正常模式，只允許點擊藍色正常狀態的鍵
                        // 灰色鍵（非 active）和紅色 pickup 狀態的鍵都需要登入
                        if (!keyState.isActive || keyState.isPickUpState) {
                          // 調試日誌已移除
                          console.log(`    - !keyState.isActive = ${!keyState.isActive} (灰色鍵)`);
                          console.log(`    - keyState.isPickUpState = ${keyState.isPickUpState} (紅色鍵)`);
                          console.log(`    - 結果：${(!keyState.isActive || keyState.isPickUpState)}`);
                          console.log('未登入用戶嘗試點擊需要登入的數字鍵，顯示登入對話框');
                          console.log('  - 🔐 調用 onRequestLogin()');
                          onRequestLogin();
                          // 調試日誌已移除
                          return;
                        }
                        
                        // 調試日誌已移除
                      } else {
                        // 調試日誌已移除
                      }
                      
                      // console.log('🔘 NumberKeyPanel 點擊鍵:', keyNo, 'isDeleteMode:', isDeleteMode);
                      // 統一調用事件處理器，將業務邏輯分離到父組件
                      onNumberKeyClick(keyNo);
                      
                      // 點選數字鍵後，自動滾動到該數字鍵的中間位置
                      setTimeout(() => {
                        // 調試日誌已移除
                        scrollToNumberKey(keyNo);
                      }, 150); // 延遲150ms，確保狀態更新完成
                    }}
                    sx={{ 
                      minWidth: '40px',
                      // 手機直式布局時縮小按鈕高度
                      height: (() => {
                        const isPortrait = window.innerWidth <= 800 && window.innerHeight >= 700;
                        return isPortrait ? '30px' : '36px';
                      })(),
                      backgroundColor: buttonColor,
                      border: '1px solid',
                      borderColor: keyState.isActive ? buttonColor : 'divider',
                      borderRadius: '8px',
                      color: textColor,
                      flexShrink: 0, // 防止按鈕被壓縮
                      // 🔧 修改：統一放大倍率為 1.3 倍
                      transform: enlargedKey === keyNo ? 'scale(1.3)' : 'scale(1)',
                      transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.2s', // 彈性動畫
                      boxShadow: enlargedKey === keyNo ? '0 6px 16px rgba(25, 118, 210, 0.6)' : 'none', // 更強陰影
                      zIndex: enlargedKey === keyNo ? 10 : 1, // 放大時浮在最上層
                      position: 'relative', // 需要 position 才能讓 z-index 生效
                      '&:hover': {
                        backgroundColor: isDeleteMode && keyState.isActive ? '#CC00CC' :
                                      isABPoint ? '#ffa000' :
                                      keyState.isPickUpState ? '#CC0000' :
                                      keyState.isActive ? '#1565c0' :
                                      '#d5d5d5',
                        borderColor: 'primary.main'
                      }
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ fontWeight: 500, fontSize: '1rem' }}
                    >
                      {keyNo}
                    </Typography>
                  </IconButton>
                </span>
            );
          })}
        </Box>
        
                {/* 滾動指示器 - 簡化版本 */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '4px', // 恢復原始位置
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: '4px',
            alignItems: 'center',
            flexDirection: 'column'
          }}
        >

          {isScrolling && (
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'primary.main',
                fontSize: '0.7rem',
                fontWeight: 'bold',
                animation: 'pulse 1s infinite'
              }}
            >
              滾動中...
            </Typography>
          )}
        </Box>
      </Box>
      
      {/* 版本管理對話框 */}
      <VersionManagementDialog
        open={versionDialogOpen}
        onClose={() => setVersionDialogOpen(false)}
        currentVersion={currentVersion}
        videoId={videoUrl || ''}
        userId={user?.uid || ''}
        onVersionAction={handleVersionAction}
      />

    </Stack>
  );
};

export default NumberKeyPanel; 